/**
 * WeaveIn.ai Platform - Navigation Page Picker Component
 * Allows workflow designers to select application pages/routes for step navigation
 */

import React, { useState, useMemo } from 'react';
import {
  ExternalLink,
  Search,
  ChevronDown,
  ChevronRight,
  User,
  FileText,
  MessageSquare,
  FolderOpen,
  Target,
  Activity,
  Heart,
  Video,
  DollarSign,
  Calendar,
  ClipboardList,
  Settings,
  Check,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select';

export interface ApplicationPage {
  path: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  dynamicParams?: string[];
}

export interface PageCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  pages: ApplicationPage[];
}

// Define all navigable application pages
export const APPLICATION_PAGES: PageCategory[] = [
  {
    id: 'enrollment',
    label: 'Enrollment',
    icon: <FileText className="w-4 h-4" />,
    pages: [
      {
        path: '/enrollments/{enrollment_id}',
        label: 'Enrollment Detail',
        description: 'Full enrollment view with all tabs',
        icon: <FileText className="w-5 h-5 text-blue-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#overview',
        label: 'Overview Tab',
        description: 'Enrollment overview and status',
        icon: <Activity className="w-5 h-5 text-blue-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#activities',
        label: 'Activities Tab',
        description: 'Activities and workflow progress',
        icon: <ClipboardList className="w-5 h-5 text-blue-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#forms',
        label: 'Forms Tab',
        description: 'Form library for enrollment',
        icon: <FileText className="w-5 h-5 text-indigo-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#conversations',
        label: 'Conversations Tab',
        description: 'SMS, Email, and Phone communications',
        icon: <MessageSquare className="w-5 h-5 text-cyan-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#documents',
        label: 'Documents Tab',
        description: 'Document upload and management',
        icon: <FolderOpen className="w-5 h-5 text-pink-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#notes',
        label: 'Notes Tab',
        description: 'Case notes and comments',
        icon: <FileText className="w-5 h-5 text-amber-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#goals',
        label: 'Goals Tab',
        description: 'Member goals and progress',
        icon: <Target className="w-5 h-5 text-green-600" />,
        dynamicParams: ['enrollment_id'],
      },
      {
        path: '/enrollments/{enrollment_id}#tracking',
        label: 'Tracking Tab',
        description: 'Member tracking and metrics',
        icon: <Activity className="w-5 h-5 text-purple-600" />,
        dynamicParams: ['enrollment_id'],
      },
    ],
  },
  {
    id: 'member',
    label: 'Member',
    icon: <User className="w-4 h-4" />,
    pages: [
      {
        path: '/members/{member_id}',
        label: 'Member Profile',
        description: 'View member details and information',
        icon: <User className="w-5 h-5 text-blue-600" />,
        dynamicParams: ['member_id'],
      },
      {
        path: '/members/{member_id}/enrollments',
        label: 'Member Enrollments',
        description: 'List of member program enrollments',
        icon: <FileText className="w-5 h-5 text-green-600" />,
        dynamicParams: ['member_id'],
      },
      {
        path: '/members/{member_id}/documents',
        label: 'Member Documents',
        description: 'Member document library',
        icon: <FolderOpen className="w-5 h-5 text-pink-600" />,
        dynamicParams: ['member_id'],
      },
      {
        path: '/members/{member_id}/activities',
        label: 'Member Activities',
        description: 'Activity history and log',
        icon: <ClipboardList className="w-5 h-5 text-amber-600" />,
        dynamicParams: ['member_id'],
      },
    ],
  },
  {
    id: 'care',
    label: 'Care Management',
    icon: <Heart className="w-4 h-4" />,
    pages: [
      {
        path: '/care-plans/{care_plan_id}',
        label: 'Care Plan',
        description: 'View or edit care plan',
        icon: <Heart className="w-5 h-5 text-red-600" />,
        dynamicParams: ['care_plan_id'],
      },
      {
        path: '/assessments',
        label: 'Assessments',
        description: 'Health assessments list',
        icon: <ClipboardList className="w-5 h-5 text-amber-600" />,
      },
      {
        path: '/assessments/{assessment_id}',
        label: 'Assessment Detail',
        description: 'Specific assessment form',
        icon: <ClipboardList className="w-5 h-5 text-amber-600" />,
        dynamicParams: ['assessment_id'],
      },
      {
        path: '/encounters/{encounter_id}',
        label: 'Encounter',
        description: 'Clinical encounter record',
        icon: <FileText className="w-5 h-5 text-blue-600" />,
        dynamicParams: ['encounter_id'],
      },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: <MessageSquare className="w-4 h-4" />,
    pages: [
      {
        path: '/telehealth',
        label: 'Telehealth',
        description: 'Video call interface',
        icon: <Video className="w-5 h-5 text-violet-600" />,
      },
      {
        path: '/telehealth/room/{room_id}',
        label: 'Telehealth Room',
        description: 'Specific video room',
        icon: <Video className="w-5 h-5 text-violet-600" />,
        dynamicParams: ['room_id'],
      },
      {
        path: '/inbox',
        label: 'Message Inbox',
        description: 'Communication center',
        icon: <MessageSquare className="w-5 h-5 text-cyan-600" />,
      },
      {
        path: '/campaigns',
        label: 'Campaigns',
        description: 'Outreach campaign management',
        icon: <Target className="w-5 h-5 text-green-600" />,
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: <DollarSign className="w-4 h-4" />,
    pages: [
      {
        path: '/billing',
        label: 'Billing Dashboard',
        description: 'Billing overview and management',
        icon: <DollarSign className="w-5 h-5 text-emerald-600" />,
      },
      {
        path: '/billing/services',
        label: 'Billing Services',
        description: 'Create and manage billing entries',
        icon: <DollarSign className="w-5 h-5 text-emerald-600" />,
      },
      {
        path: '/billing/claims',
        label: 'Claims',
        description: 'Claims management and status',
        icon: <FileText className="w-5 h-5 text-blue-600" />,
      },
      {
        path: '/billing/analytics',
        label: 'Billing Analytics',
        description: 'Revenue and billing reports',
        icon: <Activity className="w-5 h-5 text-purple-600" />,
      },
    ],
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    icon: <Calendar className="w-4 h-4" />,
    pages: [
      {
        path: '/calendar',
        label: 'Calendar',
        description: 'Staff scheduling calendar',
        icon: <Calendar className="w-5 h-5 text-blue-600" />,
      },
      {
        path: '/appointments',
        label: 'Appointments',
        description: 'Appointment management',
        icon: <Calendar className="w-5 h-5 text-green-600" />,
      },
      {
        path: '/availability',
        label: 'Availability',
        description: 'Set staff availability',
        icon: <Calendar className="w-5 h-5 text-amber-600" />,
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: <Settings className="w-4 h-4" />,
    pages: [
      {
        path: '/workflows',
        label: 'Workflow Center',
        description: 'Workflow management and tracking',
        icon: <Activity className="w-5 h-5 text-purple-600" />,
      },
      {
        path: '/forms',
        label: 'Form Templates',
        description: 'Form template library',
        icon: <FileText className="w-5 h-5 text-indigo-600" />,
      },
      {
        path: '/reports',
        label: 'Reports',
        description: 'Analytics and reports',
        icon: <Activity className="w-5 h-5 text-blue-600" />,
      },
      {
        path: '/settings',
        label: 'Settings',
        description: 'Application settings',
        icon: <Settings className="w-5 h-5 text-gray-600" />,
      },
    ],
  },
];

// Flatten all pages for lookup
export const ALL_PAGES: ApplicationPage[] = APPLICATION_PAGES.flatMap((cat) => cat.pages);

// Get page by path
export const getPageByPath = (path: string): ApplicationPage | undefined => {
  return ALL_PAGES.find((p) => p.path === path);
};

/** A route with its placeholders written out, so `/enrollments/{enrollment_id}`
 *  reads as somewhere a person recognises rather than as a template string.
 *  Module scope now: it was a closure inside the component, and the component
 *  that held it was replaced. */
const formatPathForDisplay = (path: string): string =>
  path
    .replace(/\{enrollment_id\}/g, '[Current Enrollment]')
    .replace(/\{member_id\}/g, '[Current Member]')
    .replace(/\{care_plan_id\}/g, '[Care Plan ID]')
    .replace(/\{assessment_id\}/g, '[Assessment ID]')
    .replace(/\{encounter_id\}/g, '[Encounter ID]')
    .replace(/\{room_id\}/g, '[Room ID]');

interface NavigationPagePickerProps {
  value: string | undefined;
  onChange: (path: string) => void;
  showPreview?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * The page a navigate-style step points at.
 *
 * WHY THE BESPOKE DROPDOWN IS GONE
 *
 * It rendered its panel as `absolute z-20` inside the field. In the step editor
 * the fields that come after it painted straight over the options — the form
 * type select and the e-signature checkboxes sat on top of the page list, which
 * is what this looked like in use: a dropdown you could open and could not read.
 *
 * Raising the z-index would have been the third bespoke dropdown in this
 * codebase to re-learn the same lessons. `SearchableSelect` already portals its
 * panel to the body, anchors it to the trigger, keeps it clickable inside a
 * modal that disables outside pointer events (TRUS-365), keeps it scrollable
 * against react-remove-scroll (TRUS-423), and flips it above the trigger when
 * there is no room below. Reusing it inherits all of that instead of
 * reproducing a subset of it.
 *
 * What is kept is the part that is actually this component's own: the catalogue
 * of application pages, and the preview of the chosen one.
 *
 * What is lost is the per-row icon and the collapsible categories. Groups are
 * still shown as headings, and with type-ahead over 60-odd pages a search box
 * beats folding and unfolding six categories.
 */
export const NavigationPagePicker: React.FC<NavigationPagePickerProps> = ({
  value,
  onChange,
  showPreview = true,
  placeholder,
  className = '',
}) => {
  const { t } = useAppTranslation();
  const selectedPage = useMemo(() => getPageByPath(value || ''), [value]);

  const options: SearchableSelectOption[] = useMemo(
    () =>
      APPLICATION_PAGES.flatMap((category) =>
        category.pages.map((page) => ({
          value: page.path,
          label: page.label,
          group: category.label,
          // The dynamic parameters a route needs are the thing most likely to
          // make the wrong page the wrong choice, so they stay visible on the
          // row rather than only in the preview.
          hint: page.dynamicParams?.length
            ? `${t('workflows.navigationPagePicker.uses', 'Uses')}: ${page.dynamicParams.join(', ')}`
            : undefined,
        }))
      ),
    [t]
  );

  return (
    <div className={className}>
      <SearchableSelect
        options={options}
        value={value || ''}
        onValueChange={onChange}
        placeholder={placeholder || t('workflows.navigationPagePicker.selectPage', 'Select a page...')}
        searchPlaceholder={t('workflows.navigationPagePicker.searchPages', 'Search pages...')}
        emptyText={t('workflows.navigationPagePicker.noPages', 'No page matches that')}
      />

      {showPreview && selectedPage && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
          <div className="mt-0.5 flex-shrink-0">{selectedPage.icon}</div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedPage.label}</div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">{selectedPage.description}</div>
            <div className="truncate text-xs text-gray-400">{formatPathForDisplay(selectedPage.path)}</div>
            {selectedPage.dynamicParams && selectedPage.dynamicParams.length > 0 && (
              <div className="mt-0.5 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                <ExternalLink className="h-3 w-3" />
                {t('workflows.navigationPagePicker.uses', 'Uses')}: {selectedPage.dynamicParams.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationPagePicker;
